import IncompleteType from "../../../../sports/tennis/enums/incomplete-type";
import { isDefined } from "../../../common/object-util";
import { generateAbbreviatedTeamName } from "../../../../sports/common/utils/team-utils";

export const TeamSerializer = {
    getUniform(player) {
        return isDefined(player.uniform)
            ? player.uniform
            : `auto_${player.playerId}`;
    },

    getMatchPlayers(players, singleMatches, doubleMatches, teamContext, key) {
        const { single: singlesPlayerIds, doubles: doublesPlayerIds } =
            teamContext;
        return players
            .filter((p) => p.teamId)
            .map((p) => ({
                uni: this.getUniform(p),
                name: `${p.firstName} ${p.lastName}`,
                checkName: `${p.lastName},${p.firstName}`.toUpperCase(),
                gp: [...singleMatches, ...doubleMatches].some((m) =>
                    m[key].players.some((pId) => pId === p.id)
                )
                    ? 1
                    : 0,
                id: p.playerId,
                teamId: p.teamId,
                rankNat: p.nationalRanking,
                rankConf: p.conferenceRanking,
                rankReg: p.regionalRanking,
                starter: p.starter ? 1 : 0,
                singleStatus: singlesPlayerIds.some((id) => id === p.id)
                    ? 1
                    : 0,
                doubleStatus: doublesPlayerIds.some((id) => id === p.id)
                    ? 1
                    : 0,
                singles: this.getMatchTotals(
                    singleMatches.filter((m) =>
                        m[key].players.some((pId) => pId === p.id)
                    ),
                    p.teamId,
                    key
                ),
                doubles: this.getMatchTotals(
                    doubleMatches.filter((m) =>
                        m[key].players.some((pId) => pId === p.id)
                    ),
                    p.teamId,
                    key
                )
            }));
    },

    filterMatchesByIncompleteType(matches, teamKey, type) {
        return matches.filter(
            (m) =>
                m[teamKey].incompleteType &&
                type.equals(m[teamKey].incompleteType)
        );
    },

    getMatchTotals(matches, teamId, key) {
        const winningMatches = matches.filter((m) => m.winnerId === teamId);
        const losingMatches = matches.filter(
            (m) => m.winnerId !== teamId && m.winnerId
        );

        const dqMatches = this.filterMatchesByIncompleteType(
            matches,
            key,
            IncompleteType.DQ
        );
        const dnfMatches = this.filterMatchesByIncompleteType(
            matches,
            key,
            IncompleteType.DNF
        );
        const retireMatches = this.filterMatchesByIncompleteType(
            matches,
            key,
            IncompleteType.Retired
        );
        const forfeitMatches = this.filterMatchesByIncompleteType(
            matches,
            key,
            IncompleteType.Forfeit
        );
        const dnpMatches = this.filterMatchesByIncompleteType(
            matches,
            key,
            IncompleteType.NotPlayed
        );

        return {
            won: winningMatches.length,
            lost: losingMatches.length,
            tied: matches.length - winningMatches.length - losingMatches.length,
            dq: dqMatches.length,
            dnf: dnfMatches.length,
            retire: retireMatches.length,
            forfeit: forfeitMatches.length,
            dnp: dnpMatches.length,
            matches: matches
                .sort((a, b) => a.matchNumber - b.matchNumber)
                .map((m) => ({
                    pair: m.matchNumber,
                    won: m.winnerId === teamId ? 1 : 0,
                    lost: m.winnerId && m.winnerId !== teamId ? 1 : 0,
                    tied: !m.winnerId ? 1 : 0,
                    dq: dqMatches.some((dqMatch) => dqMatch.id === m.id)
                        ? 1
                        : 0,
                    dnf: dnfMatches.some((dnfMatch) => dnfMatch.id === m.id)
                        ? 1
                        : 0,
                    retire: retireMatches.some(
                        (retireMatch) => retireMatch.id === m.id
                    )
                        ? 1
                        : 0,
                    forfeit: forfeitMatches.some(
                        (forfeitMatch) => forfeitMatch.id === m.id
                    )
                        ? 1
                        : 0,
                    dnp: dnpMatches.some((dnpMatch) => dnpMatch.id === m.id)
                        ? 1
                        : 0
                }))
        };
    },

    serialize(
        team,
        players,
        singleMatches,
        doubleMatches,
        teamContext,
        score,
        key
    ) {
        const { rpi, id, teamId, teamName, abbrName } = team;
        const { value } = rpi || {};
        return {
            vh: key === "away" ? "V" : "H",
            id: teamId,
            name: teamName,
            abbr: abbrName || generateAbbreviatedTeamName(teamName),
            code: value,
            record: team.overallRecord || null,
            rank: team.rank || null,
            rankReg: null,
            totals: {
                singles: this.getMatchTotals(singleMatches, id, key),
                doubles: this.getMatchTotals(doubleMatches, id, key),
                score: score
            },
            players: this.getMatchPlayers(
                players,
                singleMatches,
                doubleMatches,
                teamContext,
                key
            )
        };
    }
};
